import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal, untracked } from '@angular/core';
import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { RadioBoxComponent, RadioIconDirective, RadioOption } from '@libs/front/components/radiobox/radiobox.component';
import { SelectComponent, SelectOption } from '@libs/front/components/select/select.component';
import { DataTableComponent, TableColumn } from '@libs/front/components/data-table/data-table.component';
import { ButtonListComponent } from '@libs/front/components/button-list/button-list.component';
import { CheckboxComponent, CheckboxIconDirective } from '@libs/front/components/checkbox/checkbox.component';
import { UtilisateurService } from 'src/app/services/utilisateur.service';
import { DiscontinuityRef, GeologyReferenceService, LithologyRef } from 'src/app/services/new_services/geology-reference.service';
import { Hole, HoleGeologyRow } from 'src/app/services/hole.service';
import {
  GEOLOGY_FALLBACK_COLOR, GeologyInterval, GeologyKind, GeologyModel, normalizeHexColor,
} from 'src/app/components/blast3-dviewer/geology-model';

import { BlastHolesStateService, HoleCellOverlay } from '../../blast-holes-state.service';
import { HolesLegendPanelComponent } from '../../shared/holes-legend-panel/holes-legend-panel.component';
import { readColumnKeys, storeColumnKeys } from '../../shared/column-preferences';
import { HoleCellPolygon, HoleCellSite, computeHoleCells } from '../../shared/hole-cells';
import {
  discontinuityIdsInBlast,
  dominantLithologyId,
  gradientStepFor,
  hasDiscontinuity,
  intervalShares,
  intervalsByHole,
  lithologyGradient,
  lithologyIdsInBlast,
  lithologyRatioPercent,
  normalizeSampledState,
} from '../../shared/hole-geology.utils';

type GeologyMode = GeologyKind;

/** One row of the 3D legend: a layer of the block model, shown or hidden. */
interface ModelLayer {
  id:     number;
  name:   string;
  color:  string;
  /** Share of the logged length, formatted for the locale. */
  share:  string;
  hidden: boolean;
}

interface ModelLayers {
  kind:      GeologyMode;
  items:     ModelLayer[];
  anyHidden: boolean;
}

interface GeologyRow {
  publicGuid:  string;
  name:        string;
  sampled:     string;
  ratio:       string | null;
  dominant:    string | null;
  drillLog:    string | null;
}

const COLUMNS: readonly TableColumn[] = [
  { key: 'name',     label: 'QAQC_TAB.HOLE' },
  { key: 'sampled',  label: 'HOLES_TAB.GEOLOGY_COL_SAMPLED' },
  { key: 'ratio',    label: 'HOLES_TAB.GEOLOGY_COL_RATIO' },
  { key: 'dominant', label: 'HOLES_TAB.GEOLOGY_COL_DOMINANT' },
  { key: 'drillLog', label: 'HOLES_TAB.GEOLOGY_COL_DRILL_LOG' },
];

/**
 * Holes › Geology view.
 * Legend: "Geology" (one lithology, cells shaded by its share of the hole on a four-step
 * gradient built from the lithology's own colour; a hole with 0 % of it stays uncoloured)
 * or "Drill log" (one discontinuity, cells coloured where the hole logged it).
 * In 3D the viewer builds a block model of every lithology (or drill log) under the holes; the
 * legend then lists its layers, each one shown or hidden, with its share of the logged length.
 * Reference lists come from the site-scoped geology endpoints (area + local overrides); the
 * lithology dropdown only offers the ones actually sampled in the selected blast.
 * Design source : Figma 2544:672521
 */
@Component({
  selector:        'app-holes-geology-view',
  standalone:      true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers:       [DecimalPipe],
  imports: [
    FormsModule,
    NgTemplateOutlet,
    TranslateModule,
    RadioBoxComponent,
    RadioIconDirective,
    CheckboxComponent,
    CheckboxIconDirective,
    SelectComponent,
    DataTableComponent,
    ButtonListComponent,
    HolesLegendPanelComponent,
  ],
  templateUrl: './holes-geology-view.component.html',
  styleUrls:   ['./holes-geology-view.component.scss'],
})
export class HolesGeologyViewComponent {
  protected readonly state = inject(BlastHolesStateService);
  private readonly references  = inject(GeologyReferenceService);
  private readonly userService = inject(UtilisateurService);
  private readonly translate   = inject(TranslateService);
  private readonly decimal     = inject(DecimalPipe);

  private readonly lang = toSignal(
    this.translate.onLangChange.pipe(map(event => event.lang)),
    { initialValue: this.translate.currentLang },
  );

  // ── Reference lists ───────────────────────────────────────────────────────

  protected readonly lithologies     = signal<LithologyRef[]>([]);
  protected readonly discontinuities = signal<DiscontinuityRef[]>([]);
  protected readonly referencesError = signal(false);

  // ── Legend selection ──────────────────────────────────────────────────────

  protected readonly mode            = signal<GeologyMode>('lithology');
  protected readonly lithologyId     = signal<number | null>(null);
  protected readonly discontinuityId = signal<number | null>(null);

  /** Lithologies of the site that at least one hole of this blast actually samples. */
  protected readonly availableLithologies = computed<LithologyRef[]>(() => {
    const geology = this.state.geology();
    if (!geology) return [];

    const present = lithologyIdsInBlast(geology.values());
    return this.lithologies().filter(lithology => present.has(lithology.id));
  });

  /** Drill logs of the site that at least one hole of this blast actually logs. */
  protected readonly availableDiscontinuities = computed<DiscontinuityRef[]>(() => {
    const geology = this.state.geology();
    if (!geology) return [];

    const present = discontinuityIdsInBlast(geology.values());
    return this.discontinuities().filter(discontinuity => present.has(discontinuity.id));
  });

  protected readonly lithologyModeOption = computed<RadioOption[]>(() => {
    this.lang();
    return [{ value: 'lithology', label: this.translate.instant('HOLES_TAB.GEOLOGY_MODE_LITHOLOGY') }];
  });

  protected readonly drillLogModeOption = computed<RadioOption[]>(() => {
    this.lang();
    return [{ value: 'drillLog', label: this.translate.instant('HOLES_TAB.GEOLOGY_MODE_DRILL_LOG') }];
  });

  protected readonly lithologyOptions = computed<SelectOption[]>(() =>
    this.availableLithologies().map(lithology => ({ id: lithology.id, label: lithology.name })),
  );

  protected readonly discontinuityOptions = computed<RadioOption[]>(() =>
    this.availableDiscontinuities().map(discontinuity => ({ value: String(discontinuity.id), label: discontinuity.name })),
  );

  protected readonly selectedLithology = computed(() =>
    this.availableLithologies().find(lithology => lithology.id === this.lithologyId()) ?? null,
  );

  protected readonly selectedLithologyName = computed(() => this.selectedLithology()?.name ?? '');

  /** Four shades of the selected lithology's colour, ]0;25] / ]25;50] / ]50;75] / ]75;100] %. */
  protected readonly gradientSteps = computed(() => lithologyGradient(this.selectedLithology()?.color));

  protected readonly discontinuityIdValue = computed(() => {
    const id = this.discontinuityId();
    return id == null ? '' : String(id);
  });

  // ── Map overlay ───────────────────────────────────────────────────────────

  private readonly overlay = computed<HoleCellOverlay | null>(() => {
    const geology = this.state.geology();
    if (!geology) return null;

    const fills = new Map<string, string | null>();
    if (this.mode() === 'lithology') {
      const lithologyId = this.lithologyId();
      const steps = this.gradientSteps();
      if (lithologyId != null) {
        for (const [holeId, row] of geology) {
          // No samples or none of this lithology (0 %) → off the scale, no colour.
          const step = gradientStepFor(lithologyRatioPercent(row.Geology, lithologyId));
          fills.set(holeId, step == null ? null : steps[step].color);
        }
      }
    } else {
      const discontinuityId = this.discontinuityId();
      const discontinuity = this.availableDiscontinuities().find(d => d.id === discontinuityId);
      if (discontinuity) {
        for (const [holeId, row] of geology) {
          fills.set(holeId, hasDiscontinuity(row.drillLogs, discontinuity.id) ? discontinuity.color : null);
        }
      }
    }
    return { fills };
  });

  // ── 3D block model ────────────────────────────────────────────────────────

  protected readonly is3d = computed(() => this.state.mapMode() === '3d');

  private readonly hiddenLithologies     = signal<ReadonlySet<number>>(new Set());
  private readonly hiddenDiscontinuities = signal<ReadonlySet<number>>(new Set());

  /** Slices per hole, rebuilt only with the geology: hiding a layer must not rebuild the 3D model. */
  private readonly lithologyHoles = computed(() => {
    const geology = this.state.geology();
    return geology ? intervalsByHole(geology, 'lithology') : null;
  });

  private readonly drillLogHoles = computed(() => {
    const geology = this.state.geology();
    return geology ? intervalsByHole(geology, 'drillLog') : null;
  });

  /**
   * Declared length of each hole the 3D model can draw — one with a top-view cell. The legend
   * shares leave the others out, like the model does, so they describe what the 3D shows.
   */
  private readonly holeLengths = computed(() => {
    const cells = this.state.holeCells();
    return new Map(this.state.holes()
      .filter(hole => cells.has(hole.publicGuid))
      .map(hole => [hole.publicGuid, hole.Geometry?.length ?? 0]));
  });

  protected readonly lithologyLayers = computed<ModelLayers>(() =>
    this.layersOf('lithology', this.lithologyHoles(), this.lithologies(), this.hiddenLithologies()),
  );

  protected readonly discontinuityLayers = computed<ModelLayers>(() =>
    this.layersOf('drillLog', this.drillLogHoles(), this.discontinuities(), this.hiddenDiscontinuities()),
  );

  /** Name each drill log carries in the blast data, for categories missing from the site reference list. */
  private readonly drillLogNames = computed(() => {
    const names = new Map<number, string>();
    for (const row of this.state.geology()?.values() ?? []) {
      for (const log of row.drillLogs ?? []) {
        if (log.discontinuityId && log.name && !names.has(log.discontinuityId)) names.set(log.discontinuityId, log.name);
      }
    }
    return names;
  });

  /**
   * Cells of the holes placed at `sites` — where they pass at a given depth, keyed by the id of one
   * of the holes of each top-view cell — computed like the top view's. The blast contour moves with
   * the holes: it is shifted by their mean offset from their collars, so the boundary cells keep
   * following an inclined blast instead of being cut where the surface contour stands.
   */
  private readonly cellsAtDepth = (sites: readonly HoleCellSite[]): ReadonlyMap<string, HoleCellPolygon> => {
    const collars = new Map(this.state.holes()
      .filter(hole => hole.PositionPlannedXYZ)
      .map(hole => [hole.publicGuid, hole.PositionPlannedXYZ] as const));

    let dx = 0;
    let dy = 0;
    let count = 0;
    for (const site of sites) {
      const collar = collars.get(site.id);
      if (!collar) continue;
      dx += site.x - collar.x;
      dy += site.y - collar.y;
      count++;
    }
    const shiftX = count ? dx / count : 0;
    const shiftY = count ? dy / count : 0;

    const contour = this.state.contour()?.map(ring => ring.map(([x, y]) => [x + shiftX, y + shiftY]));
    return computeHoleCells(sites, contour ?? null);
  };

  private readonly model = computed<GeologyModel | null>(() => {
    const kind = this.mode();
    const holes = kind === 'lithology' ? this.lithologyHoles() : this.drillLogHoles();
    if (!holes?.size) return null;

    const references: readonly { id: number; color: string }[] = kind === 'lithology' ? this.lithologies() : this.discontinuities();
    return {
      kind,
      holes,
      // Same cells as the top view: each hole's block is centred on it.
      cells: this.state.holeCells(),
      // Drill logs follow inclined holes: cells recomputed at each depth (lithologies stay vertical).
      cellsAt: kind === 'drillLog' ? this.cellsAtDepth : undefined,
      colors: new Map(references.map(reference => [reference.id, reference.color] as const)),
      hidden: kind === 'lithology' ? this.hiddenLithologies() : this.hiddenDiscontinuities(),
    };
  });

  // ── Table ─────────────────────────────────────────────────────────────────

  protected readonly allColumns = COLUMNS as TableColumn[];
  /** User's column choice, remembered in the browser (see shared/column-preferences.ts) */
  protected readonly visibleColumnKeys = signal<Set<string>>(readColumnKeys('geology', COLUMNS, COLUMNS.map(column => column.key)));
  protected readonly visibleColumns = computed(() =>
    this.allColumns.filter(column => this.visibleColumnKeys().has(column.key)),
  );

  protected onColumnsChange(keys: Set<string>): void {
    this.visibleColumnKeys.set(keys);
    storeColumnKeys('geology', keys);
  }

  protected readonly rows = computed<GeologyRow[]>(() => {
    this.lang();
    const geology = this.state.geology();
    return this.state.holes().map(hole => this.toRow(hole, geology?.get(hole.publicGuid)));
  });

  constructor() {
    const destroyRef = inject(DestroyRef);

    // Geology data for the current blast; a new blast starts with every 3D layer shown
    effect(() => {
      const blastId = this.state.blastId();
      untracked(() => {
        this.hiddenLithologies.set(new Set());
        this.hiddenDiscontinuities.set(new Set());
        if (blastId) void this.state.loadGeology();
      });
    });

    // Reference lists for the current site
    effect(() => {
      const siteId = this.userService.currentSite()?.siteId;
      if (siteId) untracked(() => void this.loadReferences(siteId));
    });

    // Keep the dropdown on a lithology the blast actually contains
    effect(() => {
      const available = this.availableLithologies();
      const current = this.lithologyId();
      if (current != null && available.some(lithology => lithology.id === current)) return;

      const sulfur = available.find(lithology => /sulf|soufre|azufre/i.test(lithology.name));
      untracked(() => this.lithologyId.set((sulfur ?? available[0])?.id ?? null));
    });

    // Keep the radios on a drill log the blast actually contains
    effect(() => {
      const available = this.availableDiscontinuities();
      const current = this.discontinuityId();
      if (current != null && available.some(discontinuity => discontinuity.id === current)) return;

      untracked(() => this.discontinuityId.set(available[0]?.id ?? null));
    });

    // Push the computed cell colours to the map and the block model to the 3D viewer; clear them when the view is left
    effect(() => this.state.cellOverlay.set(this.overlay()));
    effect(() => this.state.geologyModel.set(this.model()));
    destroyRef.onDestroy(() => {
      this.state.cellOverlay.set(null);
      this.state.geologyModel.set(null);
    });
  }

  // ── Handlers ──────────────────────────────────────────────────────────────

  protected onModeChange(value: string): void {
    if (value === 'lithology' || value === 'drillLog') this.mode.set(value);
  }

  protected onLithologyChange(value: number | null): void {
    this.lithologyId.set(value ?? null);
    this.mode.set('lithology');
  }

  protected onDiscontinuityChange(value: string): void {
    this.discontinuityId.set(value ? Number(value) : null);
    this.mode.set('drillLog');
  }

  /** 3D legend: shows or hides one layer of the block model, and switches the model to its kind. */
  protected onLayerToggle(kind: GeologyMode, id: number, visible: boolean): void {
    this.hiddenOf(kind).update(hidden => {
      const next = new Set(hidden);
      if (visible) next.delete(id);
      else next.add(id);
      return next;
    });
    this.mode.set(kind);
  }

  protected onToggleAllLayers(layers: ModelLayers): void {
    this.hiddenOf(layers.kind).set(layers.anyHidden ? new Set() : new Set(layers.items.map(layer => layer.id)));
    this.mode.set(layers.kind);
  }

  protected onRowClick(row: GeologyRow): void {
    this.state.toggleHole(row.publicGuid);
  }

  protected retryGeology(): void {
    void this.state.loadGeology(true);
  }

  // ── Internals ─────────────────────────────────────────────────────────────

  private async loadReferences(siteId: number): Promise<void> {
    this.referencesError.set(false);
    try {
      const [lithologies, discontinuities] = await Promise.all([
        this.references.lithologies(siteId),
        this.references.discontinuities(siteId),
      ]);
      this.lithologies.set(lithologies);
      this.discontinuities.set(discontinuities);
      // Both selections are driven by the blast-scoped lists, see the constructor.
    } catch (error) {
      console.error('HolesGeologyView: unable to load geology reference lists', error);
      this.referencesError.set(true);
    }
  }

  private toRow(hole: Hole, geology: HoleGeologyRow | undefined): GeologyRow {
    const lithologyId = this.lithologyId();
    const ratio = lithologyId == null ? null : lithologyRatioPercent(geology?.Geology, lithologyId);
    const dominantId = dominantLithologyId(geology?.Geology);
    const dominant = dominantId == null ? null : this.lithologies().find(l => l.id === dominantId)?.name ?? `#${dominantId}`;

    const sampledKey = normalizeSampledState(geology?.Geology?.sampledState);
    const sampledParts = [this.translate.instant('HOLES_TAB.GEOLOGY_SAMPLED_' + sampledKey)];
    if (geology?.Geology?.isExcluded) sampledParts.push(this.translate.instant('HOLES_TAB.GEOLOGY_EXCLUDED'));

    const drillLog = geology?.drillLogs?.length
      ? geology.drillLogs
          .map(log => `${this.discontinuityName(log.discontinuityId, log.name)} ${this.format(log.fromDepth)}–${this.format(log.toDepth)} m`)
          .join('; ')
      : null;

    return {
      publicGuid: hole.publicGuid,
      name:       hole.name,
      sampled:    sampledParts.join(', '),
      ratio:      ratio == null ? null : `${this.format(ratio)} %`,
      dominant,
      drillLog,
    };
  }

  private hiddenOf(kind: GeologyMode) {
    return kind === 'lithology' ? this.hiddenLithologies : this.hiddenDiscontinuities;
  }

  /**
   * Layers of the 3D legend, largest share first: one per category the model draws, so every block
   * has a row to hide it. A category missing from the site reference lists (inactive, deleted) keeps
   * a row, with the drill log's own name or its id, in the grey the 3D model gives it.
   */
  private layersOf(
    kind: GeologyMode,
    holes: ReadonlyMap<string, readonly GeologyInterval[]> | null,
    references: readonly { id: number; name: string; color: string }[],
    hidden: ReadonlySet<number>,
  ): ModelLayers {
    const ids = new Set<number>();
    for (const intervals of holes?.values() ?? []) {
      for (const interval of intervals) ids.add(interval.id);
    }

    const lengths = this.holeLengths();
    const shares = holes ? intervalShares(holes, kind, holeId => lengths.get(holeId) ?? 0) : new Map<number, number>();
    const byId = new Map(references.map(reference => [reference.id, reference] as const));
    const logNames = kind === 'drillLog' ? this.drillLogNames() : null;

    const items = [...ids]
      .map(id => ({ id, reference: byId.get(id), share: shares.get(id) ?? 0 }))
      .sort((a, b) => b.share - a.share)
      .map(({ id, reference, share }) => ({
        id,
        name:   reference?.name ?? logNames?.get(id) ?? `#${id}`,
        color:  normalizeHexColor(reference?.color, GEOLOGY_FALLBACK_COLOR),
        share:  this.format(share),
        hidden: hidden.has(id),
      }));
    return { kind, items, anyHidden: items.some(layer => layer.hidden) };
  }

  private discontinuityName(id: number, fallback: string | null): string {
    return this.discontinuities().find(d => d.id === id)?.name ?? fallback ?? `#${id}`;
  }

  private format(value: number | null | undefined): string {
    return value == null || Number.isNaN(value) ? '–' : (this.decimal.transform(value, '1.0-1') ?? '–');
  }
}
